// @ts-strict-ignore
import React, { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Callout, RowsSkeletonLoader } from '@node-space/ui-components'
import { useDepositAccountsQuery } from 'hooks/queries'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useCustomerWalletDepositDetails } from 'pages/NestedCustomers/hooks/useCustomerWalletDepositDetails'
import { DepositBankAccount } from 'types/BankAccountsDetails'
import {
  AccountNumberTypes,
  CustomerWallet,
  Ledger,
  LedgerWalletType,
  NestedCustomerDetails,
} from 'types/customers'
import { Currencies, WalletType } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import MultipleDepositDetails from '../Deposit/components/MultipleDepositDetails'
import SingleDepositDetails from '../Deposit/components/SingleDepositDetails'

interface FiatDepositDetailsProps {
  wallet: WalletType
  customer?: NestedCustomerDetails
  isFetchingWallets: boolean
  customerWallet?: CustomerWallet
}

export const FiatDepositDetails = ({
  wallet,
  isFetchingWallets,
  customer,
  customerWallet,
}: FiatDepositDetailsProps) => {
  const { t } = useTranslation()
  const { enableLedgerV2WalletDetails } = useFeatureFlags()

  const walletLsid = customerWallet?.id || wallet?.lsid
  const useLedgerV2 = !!enableLedgerV2WalletDetails && !!walletLsid

  const trackedWalletRef = useRef<string | null>(null)

  const {
    data: principleWalletDepositDetails,
    isError: isDepositDetailsError,
    isFetching: isFetchingDepositDetails,
  } = useDepositAccountsQuery({
    walletId: wallet?.id?.toString(),
    enabled: useLedgerV2 ? !!wallet || !!customerWallet : !!wallet && !customer,
    walletLsid,
    useLedgerV2,
  })

  const { customerDepositDetails, isFetchingCountries } = useCustomerWalletDepositDetails(
    useLedgerV2 ? undefined : customer
  )

  const depositDetails = useMemo(() => {
    if (customerDepositDetails && customerWallet) {
      const fiatLedger: Ledger = customerWallet?.paymentInstruments?.find(ledger => {
        if (customerWallet?.balance?.currencyCode === Currencies.GBP) {
          return (
            ledger?.type === LedgerWalletType.FIAT &&
            ledger?.accountNumberFormat === AccountNumberTypes.SCAN
          )
        }
        return ledger?.type === LedgerWalletType.FIAT
      })

      if (fiatLedger) {
        const accountHolderName = fiatLedger?.accountHolderName || customerDepositDetails?.name

        return [
          {
            currency: customerWallet?.balance?.currencyCode,
            accountHolderName: accountHolderName,
            accountNumber: fiatLedger?.accountNumber,
            bankCode: fiatLedger?.code,
            reference: fiatLedger?.paymentReference,
            accountNumberFormat: fiatLedger?.accountNumberFormat,
            isSwift: fiatLedger?.accountNumberFormat === AccountNumberTypes.SWIFT,
            beneficiaryAddress: customerDepositDetails?.address,
          },
        ] as DepositBankAccount[]
      }
    }
  }, [customerDepositDetails, customerWallet])

  const details = useMemo(() => {
    if (useLedgerV2) {
      return principleWalletDepositDetails
    }
    if (customerWallet) {
      return depositDetails
    }
    return principleWalletDepositDetails
  }, [useLedgerV2, principleWalletDepositDetails, depositDetails, customerWallet])

  useEffect(() => {
    if (!isFetchingDepositDetails && !isDepositDetailsError && details?.length) {
      const currentWalletId = walletLsid ?? wallet?.id?.toString()
      if (trackedWalletRef.current === currentWalletId) return
      trackedWalletRef.current = currentWalletId

      track.Amp.track(AmplitudeEvent.EOF_DEPOSIT_SUCCESS, {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.VIEW,
      })
    }
  }, [isFetchingDepositDetails, isDepositDetailsError, details, walletLsid, wallet?.id])

  const isFetchingDetails = useMemo(() => {
    if (useLedgerV2) {
      return isFetchingDepositDetails || isFetchingWallets
    }
    return isFetchingDepositDetails || isFetchingWallets || (!!customer && isFetchingCountries)
  }, [useLedgerV2, isFetchingDepositDetails, isFetchingWallets, customer, isFetchingCountries])

  return (
    <Box width="full" className="my-6 space-y-4 text-gray-100">
      {isFetchingDetails && (
        <Box className="gap-0">
          <RowsSkeletonLoader rows={6} />
        </Box>
      )}

      {!isFetchingDetails && (isDepositDetailsError || !details?.length) && (
        <Box flex width={'full'} paddingT={8}>
          <Callout variant="error">{t('oopsSomethingWentWrongFetchDespoitDetails')}</Callout>
        </Box>
      )}

      {!isFetchingDetails && !isDepositDetailsError && !!details?.length && (
        <Box paddingT={8}>
          {details?.length > 1 ? (
            <MultipleDepositDetails bankAccounts={details} wallet={wallet} />
          ) : (
            <Box>
              <SingleDepositDetails account={details?.[0]} wallet={wallet} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
